import { useEffect, useRef } from 'react'
import type maplibregl from 'maplibre-gl'
import type { FeatureCollection, Feature, Polygon } from 'geojson'
import { tradeAreaService } from '../services/tradeAreaService'
import { MAP_COLORS, siteColorForIndex } from '../config/mapConfig'

export interface SiteForRings {
	id: string
	latitude: number
	longitude: number
}

export type RingVisibilityConfig = Record<string, number[]>

export interface TradeAreaRingsProps {
	map: maplibregl.Map | null
	isMapLoaded: boolean
	sites: SiteForRings[]
	visibility: RingVisibilityConfig
	visible: boolean
}

const SOURCE_ID = 'trade-area-rings-source'
const LAYER_ID_FILL = 'trade-area-rings-fill-layer'
const LAYER_ID_STROKE = 'trade-area-rings-stroke-layer'

export function TradeAreaRings({ map, isMapLoaded, sites, visibility, visible }: TradeAreaRingsProps) {
	const initialized = useRef(false)

	useEffect(() => {
		if (!map || !isMapLoaded) return

		const ringFeatures: Feature<Polygon>[] = []

		sites.forEach((site, siteIndex) => {
			if (site.latitude == null || site.longitude == null) return
			const enabledRadii = visibility[site.id]
			if (!enabledRadii || enabledRadii.length === 0) return
			const color = siteColorForIndex(siteIndex)
			const rings = tradeAreaService.generateRings(
				[site.longitude, site.latitude],
				site.id,
				color,
				enabledRadii,
			)
			rings.forEach((ring) => ringFeatures.push(ring.polygon))
		})

		const collection: FeatureCollection<Polygon> = {
			type: 'FeatureCollection',
			features: ringFeatures,
		}

		const existingSource = map.getSource(SOURCE_ID) as maplibregl.GeoJSONSource | undefined

		if (!initialized.current || !existingSource) {
			if (map.getLayer(LAYER_ID_STROKE)) map.removeLayer(LAYER_ID_STROKE)
			if (map.getLayer(LAYER_ID_FILL)) map.removeLayer(LAYER_ID_FILL)
			if (map.getSource(SOURCE_ID)) map.removeSource(SOURCE_ID)

			map.addSource(SOURCE_ID, { type: 'geojson', data: collection })

			map.addLayer({
				id: LAYER_ID_FILL,
				type: 'fill',
				source: SOURCE_ID,
				paint: {
					'fill-color': ['get', 'color'],
					'fill-opacity': MAP_COLORS.ringFill,
				},
			})
			map.addLayer({
				id: LAYER_ID_STROKE,
				type: 'line',
				source: SOURCE_ID,
				paint: {
					'line-color': ['get', 'color'],
					'line-opacity': MAP_COLORS.ringStroke,
					'line-width': MAP_COLORS.ringStrokeWidth,
				},
			})

			initialized.current = true
		} else {
			existingSource.setData(collection)
		}

		const targetVisibility = visible ? 'visible' : 'none'
		;[LAYER_ID_FILL, LAYER_ID_STROKE].forEach((id) => {
			if (map.getLayer(id)) map.setLayoutProperty(id, 'visibility', targetVisibility)
		})
	}, [map, isMapLoaded, sites, visibility, visible])

	return null
}
